"use client";

import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {useLocale, useTranslations} from "next-intl";
import {usePathname, useRouter} from "next/navigation";
import {toast} from "sonner";

import {useSession} from "@/features/account/hooks/useSession";
import {apiDelete, apiGet, apiPost} from "@/lib/apiClient";
import type {Locale} from "@/types/common";
import {withLocale} from "@/lib/localePath";

interface WishlistStatusEnvelope {
  success: true;
  data: {propertyId: string; wishlisted: boolean};
}

interface WishlistIdsEnvelope {
  success: true;
  data: {ids: string[]};
}

export const WISHLIST_LIST_QUERY_KEY = ["wishlist", "list"] as const;
export const WISHLIST_IDS_QUERY_KEY = ["wishlist", "ids"] as const;

/**
 * All wishlisted property ids for the current user, fetched once and shared
 * by every `useWishlistStatus` call on the page — a search-results grid with
 * N property cards used to fire N separate `/api/wishlist/:id/status`
 * requests (see git history); this collapses that to a single request.
 */
export function useWishlistIds() {
  const {data: session} = useSession();
  return useQuery({
    queryKey: WISHLIST_IDS_QUERY_KEY,
    queryFn: () => apiGet<WishlistIdsEnvelope>("/api/wishlist/ids").then((res) => new Set(res.data.ids)),
    enabled: Boolean(session?.authenticated),
    staleTime: 30_000
  });
}

/** Whether `propertyId` is in the current user's wishlist. Disabled entirely for logged-out visitors. */
export function useWishlistStatus(propertyId: string, options: {initialWishlisted?: boolean} = {}) {
  const idsQuery = useWishlistIds();
  const data = idsQuery.data ? idsQuery.data.has(propertyId) : options.initialWishlisted;
  return {data};
}

function updateCachedIds(queryClient: ReturnType<typeof useQueryClient>, propertyId: string, wishlisted: boolean) {
  queryClient.setQueryData<Set<string>>(WISHLIST_IDS_QUERY_KEY, (current) => {
    const next = new Set(current ?? []);
    if (wishlisted) next.add(propertyId);
    else next.delete(propertyId);
    return next;
  });
}

export function useAddToWishlist() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (propertyId: string) => apiPost<WishlistStatusEnvelope>("/api/wishlist", {propertyId}),
    onSuccess: (_result, propertyId) => {
      updateCachedIds(queryClient, propertyId, true);
      queryClient.invalidateQueries({queryKey: WISHLIST_LIST_QUERY_KEY});
    }
  });
}

export function useRemoveFromWishlist() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (propertyId: string) => apiDelete<WishlistStatusEnvelope>(`/api/wishlist/${propertyId}`),
    onSuccess: (_result, propertyId) => {
      updateCachedIds(queryClient, propertyId, false);
      queryClient.invalidateQueries({queryKey: WISHLIST_LIST_QUERY_KEY});
    }
  });
}

/**
 * Shared toggle logic behind every heart/save button. Logged-out users are
 * redirected to `/login?next=<current path>` — this app has no login modal,
 * only full-page redirects (see `middleware.ts`), so clicking the heart
 * follows the same pattern rather than introducing a new one.
 */
export function useWishlistToggle(propertyId: string, options: {initialWishlisted?: boolean} = {}) {
  const t = useTranslations("wishlist");
  const locale = useLocale() as Locale;
  const router = useRouter();
  const pathname = usePathname();
  const {data: session} = useSession();
  const statusQuery = useWishlistStatus(propertyId, options);
  const addMutation = useAddToWishlist();
  const removeMutation = useRemoveFromWishlist();

  const isWishlisted = statusQuery.data ?? false;
  const isPending = addMutation.isPending || removeMutation.isPending;

  function toggle() {
    if (!session?.authenticated) {
      router.push(withLocale(locale, `/login?next=${encodeURIComponent(pathname)}`));
      return;
    }
    if (isPending) return;

    if (isWishlisted) {
      removeMutation.mutate(propertyId, {
        onSuccess: () => {
          toast.success(t("removedToast"));
          // The status pill updates from the React Query cache above, but
          // pages like /saved render their list server-side (a plain fetch
          // in an async Server Component, not a query) — refresh so a
          // removed card actually disappears instead of lingering until a
          // manual reload. Same pattern as CancelBookingButton.
          router.refresh();
        },
        onError: (error) => toast.error(error instanceof Error ? error.message : t("removeErrorToast"))
      });
    } else {
      addMutation.mutate(propertyId, {
        onSuccess: () => {
          toast.success(t("addedToast"));
          router.refresh();
        },
        onError: (error) => toast.error(error instanceof Error ? error.message : t("addErrorToast"))
      });
    }
  }

  return {isWishlisted, isPending, toggle};
}
